/**
 * /search + collection page filter enhancements
 *
 * 1. Numeric-sort Size (Volume) and Size (Weight) facet <li> items by
 *    parsed base unit (ml for volume, g for weight) so "500 g" sits
 *    between "250 g" and "1 Kg" instead of ASCII-sorted position.
 * 2. Persist filter category open/close state within the tab session
 *    (sessionStorage). Keyed off <collapsible-row data-index=X> since
 *    that's the theme's atomic re-render unit.
 * 3. Rebind FacetRemove chip click handlers after every re-render
 *    (theme's FacetRemove.constructor binds only on initial load — after
 *    any AJAX render the new <a> children have no listeners and chip
 *    clicks degrade to full page navigation).
 * 4. FULL-SWAP renderFilters replacement — theme's per-row update-by-
 *    data-index throws when fetched HTML has a data-index not in the
 *    current DOM (routine with Phase 1's dynamic Size split + single-
 *    option group hiding). Whole-wrapper innerHTML swap avoids the
 *    mismatch and keeps re-render idempotent.
 *
 * Rules (per architecture audit):
 *   - Never touch price inputs, never dispatch input events, never call
 *     noUiSlider.set() (500ms debounced onSubmit loop risk).
 *   - Direct `details.open = X` for programmatic sets (bypasses
 *     CollapsibleRow animation to avoid stale-refs bug where its
 *     this.details points at a detached node after innerHTML replacement).
 *   - Idempotent sort — check-then-move only if out of order.
 *
 * Load: defer from layout/theme.liquid on every page.
 */
(function () {
  'use strict';

  // (Removed: STORAGE_KEY 'aro-facets-open-indexes' — replaced by
  // AroSearchAPI's label-based open filter groups so state mirrors
  // between the dropdown and /search page.)

  // ────────── Phantom price-filter suppression ──────────
  //
  // The theme's noUiSlider re-inits after every facet re-render. On re-init
  // it can dispatch a spurious 'input' event → 500ms debounced onSubmit →
  // URL update with a filter.v.price.lte the user never set. Diagnostic
  // 2026-08-07 confirmed: lte is a stale value from a previous FILTERED
  // subset (e.g. £124.43 max under "Butters and waxes"), while the current
  // slider dataMax is the full-catalogue max (£1192.49) — so a naive
  // "lte >= dataMax" check misses it.
  //
  // Real heuristic: strip incoming price params unless (a) the user
  // recently touched the slider handles, or (b) the values match what we
  // last accepted (bookmark reload / repeated identical push).
  var priceSliderTouchedAt = 0;
  var lastSeenPrice = (function () {
    var params = new URLSearchParams(location.search);
    var lte = params.get('filter.v.price.lte');
    var gte = params.get('filter.v.price.gte');
    if (lte === null && gte === null) return null;
    return { lte: lte, gte: gte };
  })();

  function markPriceSliderTouched() { priceSliderTouchedAt = Date.now(); }
  document.addEventListener('mousedown', function (e) {
    if (e.target && e.target.closest && e.target.closest('.price_slider')) markPriceSliderTouched();
  }, true);
  document.addEventListener('touchstart', function (e) {
    if (e.target && e.target.closest && e.target.closest('.price_slider')) markPriceSliderTouched();
  }, true);

  function stripPhantomPrice(searchParams) {
    try {
      var params = new URLSearchParams(searchParams);
      var lte = params.get('filter.v.price.lte');
      var gte = params.get('filter.v.price.gte');
      if (lte === null && gte === null) {
        lastSeenPrice = null;
        return params.toString();
      }
      // User dragged the slider recently → these are their values, accept.
      if (Date.now() - priceSliderTouchedAt < 2000) {
        lastSeenPrice = { lte: lte, gte: gte };
        return params.toString();
      }
      // Identical to previously-accepted values → not a new phantom, keep.
      if (lastSeenPrice && lastSeenPrice.lte === lte && lastSeenPrice.gte === gte) {
        return params.toString();
      }
      // Phantom — user didn't touch the slider, values differ from last seen.
      params.delete('filter.v.price.gte');
      params.delete('filter.v.price.lte');
      return params.toString();
    } catch (e) { return searchParams; }
  }

  // ────────── Numeric size sort ──────────

  function parseSize(label, isWeight) {
    var num = parseFloat(label);
    if (isNaN(num)) return 0;
    var l = label.toLowerCase();
    if (isWeight) {
      if (l.indexOf('kg') !== -1 || l.indexOf('kilogram') !== -1) return num * 1000;
      return num;
    }
    if (l.indexOf('ml') !== -1) return num;
    if (l.indexOf('litre') !== -1 || l.indexOf('liter') !== -1) return num * 1000;
    if (/\b\d[\d.]*\s*l\b/.test(l)) return num * 1000;
    return num;
  }

  function sortSizeFacets() {
    document.querySelectorAll('.facets .thb-filter').forEach(function (filter) {
      var titleEl = filter.querySelector('.thb-filter-title');
      if (!titleEl) return;
      var titleText = titleEl.textContent.trim().toLowerCase();
      if (titleText.indexOf('size') === -1) return;
      var isWeight = titleText.indexOf('weight') !== -1;
      var ul = filter.querySelector('.thb-filter-content ul');
      if (!ul) return;
      var items = Array.prototype.slice.call(ul.querySelectorAll(':scope > li'));
      var lastVal = -Infinity;
      var alreadySorted = true;
      for (var i = 0; i < items.length; i++) {
        var lbl = (items[i].querySelector('.thb-filter-text') || {}).textContent || '';
        var v = parseSize(lbl, isWeight);
        if (v < lastVal) { alreadySorted = false; break; }
        lastVal = v;
      }
      if (alreadySorted) return;
      items.sort(function (a, b) {
        var aL = (a.querySelector('.thb-filter-text') || {}).textContent || '';
        var bL = (b.querySelector('.thb-filter-text') || {}).textContent || '';
        return parseSize(aL, isWeight) - parseSize(bL, isWeight);
      });
      items.forEach(function (item) { ul.appendChild(item); });
    });
  }

  // ────────── Open-state persistence ──────────
  //
  // Keyed by filter LABEL (e.g. "Collection", "Availability") via
  // AroSearchAPI.getOpenFilterGroups (shared localStorage key
  // aro_open_filter_groups). Same key is used by the header dropdown's
  // predictive-search.js so open/closed state is mirrored between the
  // two surfaces — user opens Availability in the dropdown, /search
  // renders with Availability open too.
  //
  // Replaces the previous sessionStorage data-index scheme, which was
  // /search-only and didn't mirror to the dropdown.

  // Read the label of a filter group. Summary contains "Availability
  // <span><svg></span>" — we want just "Availability". Skip the span
  // by iterating text nodes only.
  function getFilterLabel(details) {
    var summary = details.querySelector('summary');
    if (!summary) return '';
    var text = '';
    Array.prototype.forEach.call(summary.childNodes, function (n) {
      if (n.nodeType === 3) text += n.nodeValue;
    });
    return text.trim();
  }

  // Mirror open/closed state only between the /search page + header
  // dropdown. Collection pages have their own default (Availability
  // open) that shouldn't be overridden by whatever the user last did
  // on /search — different pages, different browsing intent.
  function isSearchPage() {
    return /^\/search\/?$/.test(window.location.pathname);
  }

  function applyOpenState() {
    if (!isSearchPage()) return;
    var api = window.AroSearchAPI;
    if (!api || typeof api.getOpenFilterGroups !== 'function') return;
    var groups = api.getOpenFilterGroups();
    if (!groups) return; // uninitialised — leave Liquid defaults intact
    document.querySelectorAll('#FacetFiltersForm details.thb-filter').forEach(function (details) {
      var label = getFilterLabel(details);
      if (!label) return;
      var shouldOpen = groups.has(label);
      if (details.open !== shouldOpen) details.open = shouldOpen;
    });
  }

  function bindToggleListeners() {
    if (!isSearchPage()) return;
    document.querySelectorAll('#FacetFiltersForm details.thb-filter').forEach(function (details) {
      if (details.__aroToggleBound) return;
      details.__aroToggleBound = true;
      details.addEventListener('toggle', function () {
        var api = window.AroSearchAPI;
        if (!api || typeof api.setOpenFilterGroups !== 'function') return;
        var label = getFilterLabel(details);
        if (!label) return;
        var groups = api.getOpenFilterGroups();
        if (!groups) {
          // First user toggle — seed from current DOM so subsequent
          // renders reflect the user's actual preference (not just what
          // they've touched since page load).
          groups = new Set();
          document.querySelectorAll('#FacetFiltersForm details.thb-filter[open]').forEach(function (d) {
            var l = getFilterLabel(d);
            if (l) groups.add(l);
          });
        }
        if (details.open) groups.add(label);
        else groups.delete(label);
        api.setOpenFilterGroups(groups);
      });
    });
  }

  // ────────── Rebind FacetRemove chip handlers ──────────

  // Theme's FacetRemove.constructor binds click handlers to <a> children
  // once at initial page load. Every subsequent renderActiveFacets replaces
  // <facet-remove>.innerHTML — new <a> children have no listeners. Chip
  // clicks then follow the href → full page navigation. Rebind after every
  // re-render so chip removal stays AJAX.
  function rebindChipHandlers() {
    document.querySelectorAll('facet-remove').forEach(function (facetRemove) {
      facetRemove.querySelectorAll('a').forEach(function (link) {
        if (link.__aroChipBound) return;
        link.__aroChipBound = true;
        link.addEventListener('click', function (event) {
          event.preventDefault();
          var form = facetRemove.closest('facet-filters-form') || document.querySelector('facet-filters-form');
          if (form && typeof form.onActiveFilterClick === 'function') {
            form.onActiveFilterClick(event);
          }
        });
      });
    });
  }

  // ────────── Init / theme hook ──────────

  // Sync the custom Availability facet checkboxes with the URL. The
  // Liquid render always emits them unchecked; we set `checked` from
  // the current URL after every re-render — otherwise the boxes would
  // uncheck themselves while the filter is still active.
  //
  // Also enforce mutual exclusion — "In stock" and "Out of stock" are
  // semantically opposite; both checked would send contradictory URL
  // params (filter.v.availability=1 + filter.p.tag=_fully-oos = 0
  // results). The click handler unchecks the sibling so the theme's
  // form submit only sends the intended filter.
  function syncAvailabilityCheckbox() {
    var params = new URLSearchParams(location.search);
    var hasFullyOos = params.getAll('filter.p.tag').indexOf('_fully-oos') !== -1;
    var hasInStock = params.getAll('filter.v.availability').indexOf('1') !== -1;
    document.querySelectorAll('input[data-aro-availability-oos]').forEach(function (input) {
      if (input.checked !== hasFullyOos) input.checked = hasFullyOos;
      if (input.__aroExclusiveBound) return;
      input.__aroExclusiveBound = true;
      input.addEventListener('change', function () {
        if (!input.checked) return;
        document.querySelectorAll('input[data-aro-availability-instock]').forEach(function (other) {
          if (other.checked) { other.checked = false; other.dispatchEvent(new Event('change', { bubbles: true })); }
        });
      });
    });
    document.querySelectorAll('input[data-aro-availability-instock]').forEach(function (input) {
      if (input.checked !== hasInStock) input.checked = hasInStock;
      if (input.__aroExclusiveBound) return;
      input.__aroExclusiveBound = true;
      input.addEventListener('change', function () {
        if (!input.checked) return;
        document.querySelectorAll('input[data-aro-availability-oos]').forEach(function (other) {
          if (other.checked) { other.checked = false; other.dispatchEvent(new Event('change', { bubbles: true })); }
        });
      });
    });
  }

  // Inject "In stock" / "Out of stock" chips when the URL has our
  // Availability params but Shopify didn't emit a chip for them (which
  // happens on 0-result queries and when Product Tag isn't fully wired
  // in Search & Discovery). Called from runInit — after every AJAX
  // facet re-render — so the chip persists across filter changes.
  var AVAIL_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1 L9 9 M9 1 L1 9" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>';
  function buildAvailChip(label, removeUrl) {
    var a = document.createElement('a');
    a.href = removeUrl;
    a.className = 'active-facets__button';
    a.setAttribute('data-aro-avail-chip', label);
    a.textContent = label + ' ';
    var span = document.createElement('span');
    span.innerHTML = AVAIL_SVG;
    a.appendChild(span);
    return a;
  }
  function urlWithoutParam(param, value) {
    var params = new URLSearchParams(location.search);
    var kept = params.getAll(param).filter(function (v) { return v !== value; });
    params.delete(param);
    kept.forEach(function (v) { params.append(param, v); });
    var qs = params.toString();
    return location.pathname + (qs ? '?' + qs : '');
  }
  function injectAvailabilityChips() {
    // /search renders both mobile (in the side-panel drawer, off-screen
    // on desktop) AND desktop facet-remove containers. Iterate all of
    // them — injecting into only the first hits the hidden mobile one
    // on /search and the visible desktop one on collection pages, so
    // the visible desktop container gets skipped on /search.
    var containers = document.querySelectorAll('facet-remove.active-facets');
    if (!containers.length) return;
    var params = new URLSearchParams(location.search);
    var wantIn  = params.getAll('filter.v.availability').indexOf('1') !== -1;
    var wantOut = params.getAll('filter.p.tag').indexOf('_fully-oos') !== -1;

    containers.forEach(function (container) {
      var clearAll = container.querySelector('.active-facets__button-remove');
      // Remove any of our previously-injected chips first (idempotent).
      container.querySelectorAll('[data-aro-avail-chip]').forEach(function (n) { n.remove(); });

      // Does Shopify already have a chip for this filter? Detect by chip
      // TEXT (the human label) — NOT by href. Chip href is the REMOVAL
      // URL (value.url_to_remove in facets-active.liquid line 31), which
      // by definition doesn't contain the filter param being removed. So
      // an href-based match never fires, and we'd otherwise inject a
      // duplicate chip next to Shopify's own. facets-active.liquid lines
      // 25-30 relabel Shopify's chip text to "In stock" / "Out of stock"
      // server-side, so matching on that label works both when Shopify
      // emits the chip natively (normal case) and lets us skip injection.
      function shopifyChipFor(label) {
        var links = container.querySelectorAll('a.active-facets__button:not([data-aro-avail-chip]):not(.active-facets__button-remove)');
        for (var i = 0; i < links.length; i++) {
          var text = (links[i].textContent || '').trim();
          if (text.indexOf(label) === 0) return links[i];
        }
        return null;
      }
      function relabelOrInject(param, value, label) {
        var existing = shopifyChipFor(label);
        if (existing) {
          // Shopify already emitted this chip with the correct label —
          // nothing to do. Its href (value.url_to_remove) already points
          // at the correct removal URL, and FacetRemove.constructor plus
          // our rebindChipHandlers have wired up the click handler.
          return;
        }
        var chip = buildAvailChip(label, urlWithoutParam(param, value));
        if (clearAll) container.insertBefore(chip, clearAll); else container.appendChild(chip);
      }

      if (wantIn)  relabelOrInject('filter.v.availability', '1', 'In stock');
      if (wantOut) relabelOrInject('filter.p.tag', '_fully-oos', 'Out of stock');
    });
  }

  function runInit() {
    sortSizeFacets();
    bindToggleListeners();
    applyOpenState();
    injectAvailabilityChips(); // must run BEFORE rebindChipHandlers so the injected chip picks up the click handler
    rebindChipHandlers();
    syncAvailabilityCheckbox();
    // Re-populate the sidebar Recent Searches — renderProductGridContainer
    // just swapped #ProductGridContainer innerHTML, which recreated the
    // section with its default hidden attribute. Listener in
    // native-search-input.js re-reads localStorage and un-hides.
    try { window.dispatchEvent(new Event('aro:recent-updated')); } catch (e) {}
  }

  function hookRenderFilters() {
    if (!window.FacetFiltersForm || FacetFiltersForm.__aroHooked) return false;
    FacetFiltersForm.__aroHooked = true;

    // Two-hook strategy — see below for renderProductGridContainer hook.
    //
    // The desktop #FacetFiltersForm lives INSIDE #ProductGridContainer
    // (sections/main-search.liquid:30/43). The theme's renderPage calls
    // renderFilters THEN renderProductGridContainer. Anything we do to
    // desktop facets in renderFilters gets wiped when the grid container
    // is replaced ~1ms later. Mobile #FacetFiltersFormMobile is OUTSIDE
    // the grid container so it survives — we still swap it here.
    //
    // Open/closed filter state is persisted per-toggle via the label-based
    // AroSearchAPI mechanism (see bindToggleListeners above). No pre-swap
    // snapshot needed — applyOpenState runs later against the fresh DOM
    // and reads the same storage.
    FacetFiltersForm.renderFilters = function (html /* , event */) {
      try {
        var parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        // Skip desktop wrapper swap — renderProductGridContainer will
        // replace the whole #ProductGridContainer (including desktop
        // facets) with fresh HTML ~1ms after this fn returns.
        var fetchedMobile = parsedHTML.querySelector('#FacetFiltersFormMobile .facets__wrapper');
        var currentMobile = document.querySelector('#FacetFiltersFormMobile .facets__wrapper');
        if (fetchedMobile && currentMobile) currentMobile.innerHTML = fetchedMobile.innerHTML;
        if (typeof FacetFiltersForm.renderActiveFacets === 'function') FacetFiltersForm.renderActiveFacets(parsedHTML);
        if (typeof FacetFiltersForm.renderAdditionalElements === 'function') FacetFiltersForm.renderAdditionalElements(parsedHTML);
        if (typeof FacetsToggle !== 'undefined') new FacetsToggle();
        if (typeof SidebarToggle !== 'undefined') new SidebarToggle();
      } catch (e) {
        console.warn('[aro-facets] renderFilters swap failed:', e);
      }
      // Don't runInit here — desktop DOM is about to be replaced by
      // renderProductGridContainer; init runs in that hook instead.
    };

    // Hook renderProductGridContainer to run our init AFTER the grid
    // container is replaced. This is where desktop facets get their
    // fresh Liquid-default DOM, so applyOpenState needs to run against
    // that fresh DOM to restore user's open-state preferences.
    //
    // Defer via setTimeout(0) so we run AFTER FacetFiltersForm's
    // updateURLHash (sync-after-forEach in renderPage) — otherwise cached-
    // response paths make syncAvailabilityCheckbox read stale location.search.
    if (typeof FacetFiltersForm.renderProductGridContainer === 'function') {
      var origRenderPGC = FacetFiltersForm.renderProductGridContainer;
      FacetFiltersForm.renderProductGridContainer = function (html) {
        origRenderPGC.call(this, html);
        setTimeout(function () {
          try { runInit(); } catch (e) { console.warn('[aro-facets] post-grid init failed:', e); }
        }, 0);
      };
    }

    // Wrap renderPage + updateURLHash — belt-and-braces coverage of every
    // path that can push a new URL. Actual strip logic lives at module top
    // in stripPhantomPrice (touch + last-seen heuristic).
    if (typeof FacetFiltersForm.renderPage === 'function') {
      var origRenderPage = FacetFiltersForm.renderPage;
      FacetFiltersForm.renderPage = function (searchParams, event, updateURLHash) {
        return origRenderPage.call(this, stripPhantomPrice(searchParams), event, updateURLHash);
      };
    }
    if (typeof FacetFiltersForm.updateURLHash === 'function') {
      var origUpdateURLHash = FacetFiltersForm.updateURLHash;
      FacetFiltersForm.updateURLHash = function (searchParams) {
        return origUpdateURLHash.call(this, stripPhantomPrice(searchParams));
      };
    }

    // ────────── URL-filter carry-forward ──────────
    //
    // When Shopify Search & Discovery narrows to 0 matches (e.g. Publications
    // + _fully-oos yields no products), the Collection facet drops
    // "Publications" as an option — no <input value="Publications"> is
    // rendered anywhere in the DOM. URL still has main_collection=Publications,
    // but the form has no way to carry it forward on the next submit.
    // Unchecking OOS then loses Publications too and the user is dumped back
    // to unfiltered results.
    //
    // Before onSubmitHandler runs, for every URL param starting with "filter.",
    // if the (name, value) tuple has NO matching input in the form (checked
    // OR unchecked), append a hidden input to carry it forward. Skip params
    // whose input DOES exist — those represent explicit user state (a
    // deliberate uncheck must not be re-added).
    //
    // Idempotent: prior data-aro-carry inputs are cleared and re-added
    // fresh each submit so a stale carry doesn't shadow current URL state.
    if (typeof FacetFiltersForm.prototype.onSubmitHandler === 'function') {
      var origOnSubmit = FacetFiltersForm.prototype.onSubmitHandler;
      FacetFiltersForm.prototype.onSubmitHandler = function (event) {
        try {
          var form = event.target && event.target.tagName === 'FORM'
            ? event.target
            : (event.target && event.target.closest ? event.target.closest('form') : null);
          if (form) {
            form.querySelectorAll('input[data-aro-carry="1"]').forEach(function (n) { n.remove(); });
            var urlParams = new URLSearchParams(location.search);
            urlParams.forEach(function (value, key) {
              if (key.indexOf('filter.') !== 0) return;
              var esc = window.CSS && CSS.escape ? CSS.escape : function (s) { return s.replace(/["\\]/g, '\\$&'); };
              var existing = form.querySelector('input[name="' + esc(key) + '"][value="' + esc(value) + '"]');
              if (existing) return;
              var hidden = document.createElement('input');
              hidden.type = 'hidden';
              hidden.name = key;
              hidden.value = value;
              hidden.setAttribute('data-aro-carry', '1');
              form.appendChild(hidden);
            });
          }
        } catch (e) {
          console.warn('[aro-facets] carry-forward hook failed:', e);
        }
        return origOnSubmit.call(this, event);
      };
    }
    return true;
  }

  // Belt-and-braces: some analytics scripts (Clarity, Google Ads) wrap
  // pushState themselves. Wrap history.pushState / replaceState scoped to
  // /search so any URL update path is caught, not just facets.js.
  function installPushStateWrap() {
    if (history.__aroSearchPushStateHooked) return;
    if (!/^\/search\/?$/.test(window.location.pathname)) return;
    history.__aroSearchPushStateHooked = true;
    var origPushState = history.pushState;
    var origReplaceState = history.replaceState;
    function cleanUrlIfPhantom(url) {
      if (typeof url !== 'string' || url.indexOf('filter.v.price') === -1) return url;
      var qIndex = url.indexOf('?');
      if (qIndex === -1) return url;
      var qs = url.slice(qIndex + 1);
      var cleaned = stripPhantomPrice(qs);
      return cleaned !== qs ? url.slice(0, qIndex + 1) + cleaned : url;
    }
    history.pushState = function (state, title, url) {
      return origPushState.call(this, state, title, cleanUrlIfPhantom(url));
    };
    history.replaceState = function (state, title, url) {
      return origReplaceState.call(this, state, title, cleanUrlIfPhantom(url));
    };
  }

  // "Did you mean X?" — only on /search zero-result pages. Uses Shopify's
  // /search/suggest.json to fetch query suggestions and, if the top
  // suggestion differs from the user's typed term (case-insensitive), shows
  // it as a clickable link into the empty-state message. Silent if suggest
  // returns nothing useful or if the suggestion IS the query (typo-tolerant
  // exact match). No product-title fallback — noisy and typically returns
  // full product titles that make the "Did you mean" text unreadable.
  function fetchDidYouMean(query) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(query)
      + '&resources[type]=query&resources[limit]=5';
    return fetch(url).then(function (r) {
      if (!r.ok) return null;
      return r.json();
    }).then(function (json) {
      var queries = (json && json.resources && json.resources.results && json.resources.results.queries) || [];
      var typed = query.trim().toLowerCase();
      for (var i = 0; i < queries.length; i++) {
        var q = (queries[i].text || queries[i].title || '').trim();
        if (q && q.toLowerCase() !== typed) return q;
      }
      return null;
    }).catch(function () { return null; });
  }

  function showDidYouMean() {
    if (!isSearchPage()) return;
    var target = document.querySelector('[data-aro-did-you-mean]');
    if (!target) return; // No 0-result state on this page
    var params = new URLSearchParams(window.location.search);
    var query = (params.get('q') || '').trim();
    if (!query) return;
    fetchDidYouMean(query).then(function (suggestion) {
      if (!suggestion) return;
      target.innerHTML = '';
      target.appendChild(document.createTextNode('Did you mean '));
      var link = document.createElement('a');
      link.href = '/search?q=' + encodeURIComponent(suggestion);
      link.className = 'aro-did-you-mean__link';
      link.textContent = '"' + suggestion + '"';
      target.appendChild(link);
      target.appendChild(document.createTextNode('?'));
      target.removeAttribute('hidden');
    });
  }

  // Save the current /search query to recent searches on page load.
  // Zero-result queries are filtered out by AroSearchAPI.addRecentSearch
  // so the list stays clean of typos and gibberish.
  function saveCurrentQueryToRecent() {
    if (!/^\/search\/?$/.test(window.location.pathname)) return;
    var api = window.AroSearchAPI;
    if (!api || typeof api.addRecentSearch !== 'function') return;
    var params = new URLSearchParams(window.location.search);
    var query = (params.get('q') || '').trim();
    if (!query) return;
    // Result count from the server-rendered `results--N` class on
    // #Sidebar-Container (main-search.liquid:130 — always present on
    // /search, populated from search.results.size across all types).
    // Reliable at bootstrap regardless of query outcome.
    //
    // Fall back to 0 (don't save) if we can't parse — err on the side
    // of a clean Recent Searches list vs risking pollution from an
    // unexpected DOM. Previous fallback of count=1 caused zero-result
    // queries with titles like "No results found for 'zzz'" (no digit
    // for the regex to grab) to leak into the list. Contract with the
    // API: count <= 0 is filtered by addRecentSearch (native-search-
    // api.js:135).
    var count = 0;
    var sidebar = document.getElementById('Sidebar-Container');
    var m = sidebar && sidebar.className.match(/results--(\d+)/);
    if (m) count = parseInt(m[1], 10);
    api.addRecentSearch(query, count);
    // native-search-input.js runs its Recent Searches populator on
    // DOMContentLoaded, which may fire BEFORE this save. Notify it so
    // it re-reads localStorage and un-hides the section.
    try { window.dispatchEvent(new Event('aro:recent-updated')); } catch (e) { /* IE11 fallback not needed */ }
    // No F13 analytics fire from /search bootstrap — strict CC parity:
    // CC only pushed on header form submit. Direct-nav searches to
    // /search (bookmark, Popular / Recent click, external link) don't
    // fire an analytics event, matching pre-native behaviour.
  }

  function bootstrap() {
    installPushStateWrap();
    runInit();
    saveCurrentQueryToRecent();
    showDidYouMean();
    // Re-apply open state if another surface (dropdown) toggled while
    // we're on /search — keeps the sidebar in sync in real time.
    window.addEventListener('aro:open-groups-updated', applyOpenState);
    // Cross-tab too.
    window.addEventListener('storage', function (e) {
      if (e.key === 'aro_open_filter_groups') applyOpenState();
    });
    if (!hookRenderFilters()) {
      var tries = 0;
      var iv = setInterval(function () {
        tries++;
        if (hookRenderFilters() || tries > 40) clearInterval(iv);
      }, 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
